import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import type { DocumentData, Firestore } from 'firebase/firestore';

/**
 * Rychlé úkoly pro celý Stakko.
 *
 * Ukládají se do stejného dokumentu, který používá modul „Vše“ ve Všerozvhu:
 *   users/{uid}/data/todos  →  { list: StakkoTodo[] }
 *
 * Tvar položky je záměrně shodný s Todo z modulu Vše, takže se quick todos
 * a úkoly v rozvrhu zobrazují a synchronizují oboustranně.
 */
export interface StakkoTodo {
  id: number;
  /** Předmět / štítek (nepovinné). */
  subj: string;
  text: string;
  /** Termín ve formátu YYYY-MM-DD (nepovinné). */
  date: string;
  done: boolean;
}

export interface StakkoTodoInput {
  text: string;
  subj?: string;
  date?: string;
}

export function stakkoTodosDocRef(db: Firestore, uid: string) {
  return doc(db, 'users', uid, 'data', 'todos');
}

function normalizeTodo(raw: unknown, fallbackId: number): StakkoTodo | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  const text =
    typeof value.text === 'string'
      ? value.text
      : typeof value.title === 'string'
        ? value.title
        : '';
  if (!text.trim()) return null;

  return {
    id: typeof value.id === 'number' ? value.id : fallbackId,
    subj: typeof value.subj === 'string' ? value.subj : '',
    text,
    date: typeof value.date === 'string' ? value.date : '',
    done: value.done === true,
  };
}

export function normalizeStakkoTodos(raw: unknown): StakkoTodo[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => normalizeTodo(item, Date.now() + index))
    .filter((item): item is StakkoTodo => item !== null);
}

function parseTodos(data: DocumentData | undefined): StakkoTodo[] {
  if (!data || typeof data !== 'object') return [];
  return normalizeStakkoTodos((data as { list?: unknown }).list);
}

/** Živý odběr společného seznamu úkolů. */
export function subscribeStakkoTodos(
  db: Firestore,
  uid: string,
  onData: (todos: StakkoTodo[]) => void,
): () => void {
  return onSnapshot(
    stakkoTodosDocRef(db, uid),
    (snap) => onData(parseTodos(snap.data())),
    (error) => {
      console.warn('[stakko] todos se nepodařilo načíst', error);
      onData([]);
    },
  );
}

/** Jednorázové načtení (např. při přihlášení). */
export async function loadStakkoTodos(db: Firestore, uid: string): Promise<StakkoTodo[]> {
  const snap = await getDoc(stakkoTodosDocRef(db, uid));
  return parseTodos(snap.data());
}

/** Uloží celý seznam do users/{uid}/data/todos. */
export async function saveStakkoTodos(
  db: Firestore,
  uid: string,
  todos: StakkoTodo[],
): Promise<void> {
  await setDoc(stakkoTodosDocRef(db, uid), { list: todos }, { merge: true });
}

/** Otevřené první, pak podle termínu. */
export function sortStakkoTodos(todos: StakkoTodo[]): StakkoTodo[] {
  return [...todos].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    if (a.date && b.date) return new Date(a.date).getTime() - new Date(b.date).getTime();
    if (a.date) return -1;
    if (b.date) return 1;
    return 0;
  });
}

function nextTodoId(todos: StakkoTodo[]): number {
  const max = todos.reduce((acc, todo) => Math.max(acc, todo.id), 0);
  return Math.max(Date.now(), max + 1);
}

export async function addStakkoTodo(
  db: Firestore,
  uid: string,
  todos: StakkoTodo[],
  input: StakkoTodoInput,
): Promise<StakkoTodo[]> {
  const text = input.text.trim();
  if (!text) return todos;
  const updated = [
    ...todos,
    {
      id: nextTodoId(todos),
      subj: (input.subj ?? '').trim(),
      text,
      date: (input.date ?? '').trim(),
      done: false,
    },
  ];
  await saveStakkoTodos(db, uid, updated);
  return updated;
}

export async function toggleStakkoTodo(
  db: Firestore,
  uid: string,
  todos: StakkoTodo[],
  id: number,
): Promise<StakkoTodo[]> {
  const updated = todos.map((todo) => (todo.id === id ? { ...todo, done: !todo.done } : todo));
  await saveStakkoTodos(db, uid, updated);
  return updated;
}

export async function deleteStakkoTodo(
  db: Firestore,
  uid: string,
  todos: StakkoTodo[],
  id: number,
): Promise<StakkoTodo[]> {
  const updated = todos.filter((todo) => todo.id !== id);
  await saveStakkoTodos(db, uid, updated);
  return updated;
}
